/**
 * Consequence lines (M8 plan TR-5): a sale that unblocks a starved production line tells the player so, as one toast
 * line (no Wire post: that would need a worker command).
 *
 * - The runtime (`career/runtime.ts`) keeps the station's `StationView` from before the sale and compares it with
 *   each later live view of that station until a line restarts (or the pilot launches, sells elsewhere, or the career
 *   changes).
 * - A line counts as **unblocked** when it was `starved` before (on one of the sold wares, when `wares` is given) and
 *   is `running` after, matched by index and recipe. A line that is still starved (another input now missing) or
 *   blocked (an output full) did not restart.
 * - At most one line per comparison: one restarted line is named by its output, several are counted. Nothing changed
 *   gives an empty list.
 *
 * Pure; called on views (about once a second at most), never per frame, so it may allocate.
 */
import modulesJson from '../data/strategic/modules.json'
import waresJson from '../data/strategic/wares.json'
import type { LineView, StationView } from '../strategic/types'

/** The player-facing consequence lines; `{name}` fields are filled in. */
export const CONSEQUENCE_TEXTS = {
  one: 'Your {ware} restarted the {output} line at {station}.',
  many: 'Your delivery restarted {count} starved production lines at {station}.',
  /** A line whose recipe output is unknown to the data (should not happen). */
  unknownOutput: 'production',
} as const

interface RecipeOutputs {
  readonly id: string
  readonly outputs: Readonly<Record<string, number | undefined>>
}

const WARE_NAMES: ReadonlyMap<string, string> = new Map(waresJson.wares.map((w) => [w.id, w.name]))

/** Recipe id → its first output's ware id (`strategic/modules.json`). */
const RECIPE_OUTPUT: ReadonlyMap<string, string> = new Map(
  modulesJson.modules
    .flatMap((m): readonly RecipeOutputs[] => ('recipes' in m ? (m.recipes ?? []) : []))
    .map((r) => [r.id, Object.keys(r.outputs)[0] ?? ''] as const),
)

function fill(text: string, fields: Readonly<Record<string, string | number>>): string {
  return text.replace(/\{(\w+)\}/g, (match: string, key: string) => (key in fields ? String(fields[key]) : match))
}

function wareName(id: string): string {
  return WARE_NAMES.get(id) ?? id
}

/** True when line `b` (before) was starved on one of `wares` (any ware when absent) and `a` (after) runs it again. */
function restarted(b: LineView, a: LineView | undefined, wares: readonly string[] | undefined): boolean {
  if (a === undefined || a.recipe !== b.recipe) return false
  if (b.status !== 'starved' || a.status !== 'running') return false
  return wares === undefined || (b.missing !== null && wares.includes(b.missing))
}

/** The lines of `before` that `after` shows restarted (see the file header); none when they are different stations. */
export function restartedLines(before: StationView, after: StationView, wares?: readonly string[]): LineView[] {
  if (before.id !== after.id) return []
  return before.lines.filter((b, i) => restarted(b, after.lines[i], wares))
}

/** True when `view` has a line starved on one of `wares`: a sale of them may restart it (worth watching). */
export function starvedOn(view: StationView, wares: readonly string[]): boolean {
  return view.lines.some((l) => l.status === 'starved' && l.missing !== null && wares.includes(l.missing))
}

/**
 * The toast lines for a sale that changed `before` into `after`: exactly one line when the sale (of `wares`, when
 * given) restarted one or more starved lines, else none.
 */
export function consequenceLines(before: StationView, after: StationView, wares?: readonly string[]): string[] {
  const lines = restartedLines(before, after, wares)
  if (lines.length === 0) return []
  if (lines.length > 1) return [fill(CONSEQUENCE_TEXTS.many, { count: lines.length, station: after.name })]
  const line = lines[0]
  const output = RECIPE_OUTPUT.get(line.recipe)
  return [
    fill(CONSEQUENCE_TEXTS.one, {
      ware: wareName(line.missing ?? ''),
      output: output === undefined || output === '' ? CONSEQUENCE_TEXTS.unknownOutput : wareName(output),
      station: after.name,
    }),
  ]
}
